import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, CheckCircle2, TrendingUp, AlertCircle, Compass } from 'lucide-react';

/**
 * EnergyControlPreview (Phase 6 — simplified preview).
 * Frontend-only restructure: same backend `preview` payload, friendlier UX.
 *
 * Renders:
 *  - 1 confirmation strip
 *  - 1 hoofdconclusie + 1 korte subregel
 *  - 3 samenvattingsblokken: Grootste kans / Aandachtspunt / Aanbevolen eerste stap
 *  - max 3 concrete aanbevelingen
 *  - 1 CTA
 *
 * Derivation rules (pure presentation logic on top of preview.badges/opportunities):
 *  - Grootste kans  : priorities ev_charging > heatpump > boiler > not_home_opportunity
 *  - Aandachtspunt  : priorities measure_first > peak_load_attention
 *  - Eerste stap    : derived from the above + control_preference signals.
 */

const OPP_TITLE = {
  ev_charging_opportunity: 'EV slim laden op eigen zonnestroom',
  boiler_buffer: 'Boiler als zonne-buffer gebruiken',
  heatpump_comfort_limit: 'Warmtepomp slim sturen, comfort behouden',
  not_home_opportunity: 'Verbruik tijdens niet-thuis benutten',
  peak_load_attention: 'Pieken voorkomen door spreiden',
  measure_first: 'Eerst meten met een P1-uitlezing',
};

const OPP_SHORT = {
  ev_charging_opportunity: 'Vaak de grootste maandelijkse besparing.',
  boiler_buffer: 'Gratis "thermische batterij" voor uw zonneoverschot.',
  heatpump_comfort_limit: 'Verschuif uren binnen veilige comfortgrens.',
  not_home_opportunity: 'Laat apparaten draaien als panelen volop leveren.',
  peak_load_attention: 'Voorkom capaciteitstarief en netcongestie.',
  measure_first: 'Zonder meten geen sturing — kleine investering, groot effect.',
};

const FIRST_STEP_FALLBACK = 'Bekijk uw volledige Energy Control Plan voor het stappenplan.';

function deriveSummary(badges = [], opportunities = []) {
  const oppIds = new Set(opportunities.map((o) => o.id));
  const badgeIds = new Set(badges.map((b) => b.id));

  // ---- Grootste kans (priority order)
  const chancePriority = [
    'ev_charging_opportunity',
    'heatpump_comfort_limit',
    'boiler_buffer',
    'not_home_opportunity',
  ];
  const chanceId = chancePriority.find((id) => oppIds.has(id));
  const biggest_chance = chanceId
    ? { title: OPP_TITLE[chanceId], body: OPP_SHORT[chanceId] }
    : badgeIds.has('big_consumers_present')
      ? { title: 'Slim sturen op grootverbruikers', body: 'U heeft apparaten met directe besparingsruimte.' }
      : badgeIds.has('solar_present')
        ? { title: 'Meer eigen zonnestroom direct gebruiken', body: 'Minder terugleveren = lagere maandkosten.' }
        : { title: 'Slim contract en tarieven benutten', body: 'Dynamische uren of variabel tarief levert structureel voordeel.' };

  // ---- Aandachtspunt
  const attentionPriority = ['measure_first', 'peak_load_attention'];
  const attentionId = attentionPriority.find((id) => oppIds.has(id));
  const attention_point = attentionId
    ? { title: OPP_TITLE[attentionId], body: OPP_SHORT[attentionId] }
    : { title: 'Geen acuut aandachtspunt', body: 'Uw situatie is vrij gunstig — het draait om optimalisatie.' };

  // ---- Aanbevolen eerste stap
  let first_step;
  if (oppIds.has('measure_first')) {
    first_step = { title: 'Plaats een P1-uitlezing', body: 'Eerst zien wat er gebeurt, dan pas sturen.' };
  } else if (oppIds.has('ev_charging_opportunity')) {
    first_step = { title: 'Activeer slim laden voor uw EV', body: 'Begin met de grootste besparing.' };
  } else if (oppIds.has('not_home_opportunity')) {
    first_step = { title: 'Plan zonnemomenten voor grootverbruikers', body: 'Vaatwasser, boiler of EV op piek-opwek.' };
  } else if (oppIds.has('heatpump_comfort_limit')) {
    first_step = { title: 'Stel een veilige comfortgrens in', body: 'Slim sturen zonder koude woning.' };
  } else if (badgeIds.has('auto_switching_possible')) {
    first_step = { title: 'Activeer slim schakelen op 1 apparaat', body: 'Klein beginnen, daarna opschalen.' };
  } else {
    first_step = { title: 'Bekijk uw volledige plan', body: FIRST_STEP_FALLBACK };
  }

  return { biggest_chance, attention_point, first_step };
}

function topRecommendations(opportunities = [], summary, limit = 3) {
  // Filter out the opportunity that is already shown as "Grootste kans"
  // — avoid duplication.
  const exclude = new Set();
  if (summary?._chance_id) exclude.add(summary._chance_id);
  return opportunities.filter((o) => !exclude.has(o.id)).slice(0, limit);
}

function SummaryBlock({ icon: Icon, label, accent, title, body, testId }) {
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border ${accent.border} ${accent.bg} px-4 py-3.5`}
      data-testid={testId}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${accent.iconBg}`}>
        <Icon className={`w-5 h-5 ${accent.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[11px] uppercase tracking-wider font-medium mb-0.5 ${accent.label}`}>
          {label}
        </div>
        <div className="text-[15px] font-medium text-foreground leading-snug" data-testid={`${testId}-title`}>
          {title}
        </div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
          {body}
        </div>
      </div>
    </div>
  );
}

export default function EnergyControlPreview({ intakeId, preview, source = 'submit' }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.pwTrack) window.pwTrack('energy_control_preview_view', { intake_id: intakeId, source });
  }, [intakeId, source]);

  const summary = useMemo(() => deriveSummary(preview?.badges, preview?.opportunities), [preview]);
  const recs = useMemo(() => topRecommendations(preview?.opportunities, summary), [preview, summary]);

  if (!preview) return null;

  const handleCta = () => {
    if (window.pwTrack) window.pwTrack('energy_control_report_open', { intake_id: intakeId });
    navigate(`/energy-control-report/${intakeId}`);
  };

  return (
    <div className="container mx-auto px-6 py-10">
      <div className="max-w-2xl mx-auto" data-testid="ec-preview">
        {/* Confirmation strip */}
        <div
          className="flex items-start gap-2 bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 mb-6 text-sm text-primary/90"
          data-testid="ec-saved-strip"
        >
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Uw Energy Control intake is opgeslagen.</span>
        </div>

        {/* Header — 1 conclusie + 1 subregel */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-border">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-xs uppercase tracking-wider text-primary/70 font-medium">
              Gratis preview
            </span>
          </div>
          <h1
            className="text-2xl sm:text-[28px] font-medium text-primary mb-2 leading-tight"
            style={{ fontFamily: 'Outfit' }}
            data-testid="ec-preview-title"
          >
            {preview.title}
          </h1>
          <p
            className="text-sm sm:text-base text-muted-foreground leading-relaxed"
            data-testid="ec-preview-lead"
          >
            {preview.lead}
          </p>

          {/* 3 samenvattingsblokken */}
          <div className="mt-7 space-y-3" data-testid="ec-preview-summary">
            <SummaryBlock
              icon={TrendingUp}
              label="Grootste kans"
              accent={{
                border: 'border-emerald-200',
                bg: 'bg-emerald-50/60',
                iconBg: 'bg-emerald-100',
                iconColor: 'text-emerald-700',
                label: 'text-emerald-700',
              }}
              title={summary.biggest_chance.title}
              body={summary.biggest_chance.body}
              testId="ec-summary-chance"
            />
            <SummaryBlock
              icon={AlertCircle}
              label="Aandachtspunt"
              accent={{
                border: 'border-amber-200',
                bg: 'bg-amber-50/60',
                iconBg: 'bg-amber-100',
                iconColor: 'text-amber-700',
                label: 'text-amber-700',
              }}
              title={summary.attention_point.title}
              body={summary.attention_point.body}
              testId="ec-summary-attention"
            />
            <SummaryBlock
              icon={Compass}
              label="Aanbevolen eerste stap"
              accent={{
                border: 'border-sky-200',
                bg: 'bg-sky-50/60',
                iconBg: 'bg-sky-100',
                iconColor: 'text-sky-700',
                label: 'text-sky-700',
              }}
              title={summary.first_step.title}
              body={summary.first_step.body}
              testId="ec-summary-first-step"
            />
          </div>

          {/* Max 3 concrete aanbevelingen */}
          {recs.length > 0 && (
            <div className="mt-8" data-testid="ec-preview-recommendations">
              <div className="text-[11px] uppercase tracking-wider text-primary/70 font-medium mb-3">
                Top 3 aanbevelingen
              </div>
              <ol className="space-y-2.5">
                {recs.map((o, idx) => (
                  <li
                    key={o.id}
                    className="flex items-start gap-3"
                    data-testid={`ec-rec-${o.id}`}
                  >
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground leading-snug">
                        {o.title}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">
                        {o.body}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* CTA */}
          <div className="mt-8 pt-6 border-t">
            <button
              onClick={handleCta}
              className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-[15px] font-medium"
              data-testid="ec-preview-cta"
            >
              {preview.cta_label || 'Bekijk mijn volledige Energy Control Plan'}
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-muted-foreground mt-3">
              Geen abonnement · geen verplichtingen · uw intake blijft 24 maanden bewaard
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
